import { Injectable } from '@angular/core';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import { Store } from '@ngxs/store';
import {
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import { buildHandleBarModel, GenericHandleBarModel } from '../../utils/template/model-builder.utils';
import { flattenObject } from '../../utils/utils';
import { AccountService } from '../account-service/account-service.service';
import { CountriesCurrenciesService } from '../countries-currencies-service/countries-currencies.service';
import { WorkflowFormsService } from '../workflow-forms/workflow-forms.service';
import { responsiveHideHelpers } from '@app/finxone-web-frontend/app/lib/utils/template/handlebar-helpers/responsive-hide.helpers';

@Injectable({
  providedIn: 'root',
})
export class ResponsiveHideConditionsService {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  private readonly handlebarModel$: Observable<GenericHandleBarModel>;
  constructor(
    private readonly workflowFormsService: WorkflowFormsService,
    private readonly store: Store,
    private readonly countriesCurrenciesService: CountriesCurrenciesService,
    private readonly iconCacheService: IconCacheService,
    private readonly accountService: AccountService,
  ) {
    // Get the handlebar model which contains all the state data (accounts, profile, cards, etc.)
    this.handlebarModel$ = buildHandleBarModel(
      this.store,
      '',
      this.countriesCurrenciesService,
      this.iconCacheService,
      this.accountService,
      undefined,
      false,
      this.workflowFormsService,
      true, // noAutoRefreshStateData - we don't want to trigger state refreshes from hide conditions
    );
  }

  public setupHideCondition(condition: string): Observable<boolean> {
    try {
      new Function(`return !!(${condition})`); // validate syntax only, don't call it
    } catch {
      return of(false);
    }

    const rootForms = this.extractRootNames(condition);
    const formValueObservables$ = rootForms.map((root) => this.observeRootFormValue(root));

    return combineLatest([this.handlebarModel$, ...formValueObservables$]).pipe(
      map(([handlebarModel, ...formValues]) => {
        const ctx: Record<string, any> = {
          ...handlebarModel,
          ...responsiveHideHelpers,
        };

        rootForms.forEach((root, index) => {
          ctx[root] = flattenObject(formValues[index]) ?? {};
        });

        // null from helpers below means "data not loaded yet" — forces stayHidden=true, prevents flicker; don't strip this
        let dataNotReady = false;
        const nullTrackingHelpers: Record<string, any> = {};
        const helperNames = Object.keys(responsiveHideHelpers);

        helperNames.forEach((name) => {
          const originalFn = ctx[name]; // capture before Object.assign can overwrite it
          if (originalFn) {
            nullTrackingHelpers[name] = (...args: any[]) => {
              const result = originalFn(...args);
              if (result === null) dataNotReady = true;
              return result ?? 0;
            };
          }
        });

        Object.assign(ctx, nullTrackingHelpers);

        try {
          const validIdentifier = /^[A-Za-z_$][A-Za-z0-9_$]*$/;
          const entries = Object.entries(ctx).filter(([k]) => validIdentifier.test(k));
          const keys = entries.map(([k]) => k);
          const values = entries.map(([, v]) => v);
          const fn = new Function(...keys, `return !!(${condition})`);
          const result = fn(...values);
          if (dataNotReady) return true;
          return result;
        } catch {
          return false;
        }
      }),
      distinctUntilChanged(),
    );
  }

  private observeRootFormValue(root: string): Observable<any> {
    return this.workflowFormsService.observeForm(root).pipe(
      switchMap((form) => {
        if (!form) {
          // If the form isn't created yet, expose an empty object until it exists
          return of({});
        }
        // Emit current value immediately, then subsequent changes
        return form.valueChanges.pipe(startWith(form.value));
      }),
    );
  }

  private extractRootNames(expression: string): string[] {
    // Remove string literals to avoid matching identifiers inside quotes
    const withoutStrings = expression.replace(/(["'`])(?:\\.|(?!\1).)*\1/g, ' ');
    // Match identifiers and dot paths, then take the first segment as the root name
    const candidates = withoutStrings.match(/\b[A-Za-z_]\w*(?:\.\w+)*\b/g) ?? [];
    const reserved = new Set(['true', 'false', 'null', 'undefined']);
    const roots = new Set<string>();
    const currentPageForms = this.workflowFormsService.getCurrentPageForms();
    for (const token of candidates) {
      const root = token.split('.')[0];
      //find if the root form is in the current page forms
      const rootFormIsInCurrentPage = currentPageForms.some((form) => form.includes(root));
      //determine if the form should be used
      const formShouldBeUsed =
        rootFormIsInCurrentPage && !roots.has(root) && !reserved.has(root) && isNaN(Number(root));
      if (formShouldBeUsed) {
        roots.add(root);
      }
    }
    return Array.from(roots);
  }
}
